import { useState, useEffect } from 'react'
import EmailHtmlEditor from '../../components/EmailHtmlEditor'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Text, Spinner, Button, makeStyles, tokens,
  MessageBar, MessageBarBody,
  Combobox, Option, Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions,
} from '@fluentui/react-components'
import {
  Settings24Regular, Save24Regular, CheckmarkCircle24Regular, Mail24Regular,
  Add24Regular, Delete24Regular, CalendarLtr24Regular,
  ChevronUp20Regular, ChevronDown20Regular,
} from '@fluentui/react-icons'
import { configService, CONFIG_KEYS } from '../../services/configService'
import { employeesService } from '../../services/employeesService'
import { contractorsService } from '../../services/contractorsService'
import { publicHolidaysService } from '../../services/publicHolidaysService'
import { vocativeExceptionsService, VocativeException } from '../../services/vocativeExceptionsService'

const useStyles = makeStyles({
  page:    { maxWidth: '800px' },
  header:  { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '28px' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    boxShadow: tokens.shadow4,
    marginBottom: '16px',
    overflow: 'visible',
  },
  cardHeader: {
    padding: '14px 24px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  cardTitle: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  cardBody:  { padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' },
  label: {
    fontSize: '11px', fontWeight: '600', textTransform: 'uppercase',
    letterSpacing: '.5px', color: tokens.colorNeutralForeground3, marginBottom: '4px',
  },
  description: { fontSize: '12px', color: tokens.colorNeutralForeground3, marginTop: '4px' },
  input: {
    width: '100%', padding: '9px 12px',
    border: `1.5px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: tokens.fontFamilyBase, fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1, backgroundColor: tokens.colorNeutralBackground1,
    outline: 'none', boxSizing: 'border-box' as const,
  },
  row:   { display: 'flex', gap: '10px', alignItems: 'flex-end' },
  savedHint: { fontSize: '12px', color: tokens.colorPaletteGreenForeground1, display: 'flex', alignItems: 'center', gap: '4px' },
  templateSection: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingBottom: '20px',
    marginBottom: '4px',
  },
  templateLabel: {
    fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold,
    marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px',
  },
  varHint: {
    fontSize: '11px', color: tokens.colorNeutralForeground3, marginBottom: '10px',
    background: tokens.colorNeutralBackground2, padding: '6px 10px',
    borderRadius: tokens.borderRadiusMedium, fontFamily: 'monospace',
  },
})

interface ConfigItemProps {
  configKey: string
  label: string
  description: string
  currentValue: string
}

function ConfigItem({ configKey, label, description, currentValue }: ConfigItemProps) {
  const styles = useStyles()
  const qc = useQueryClient()
  const [value, setValue] = useState(currentValue)
  const [saved, setSaved] = useState(false)

  const mutation = useMutation({
    mutationFn: () => configService.upsert(configKey, value),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['config'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })

  const isDirty = value !== currentValue

  return (
    <div>
      <div className={styles.label}>{label}</div>
      <div className={styles.row}>
        <input
          className={styles.input}
          value={value}
          onChange={e => { setValue(e.target.value); setSaved(false) }}
          style={{ flex: 1 }}
        />
        <Button
          appearance="primary"
          icon={mutation.isPending ? <Spinner size="tiny" /> : <Save24Regular />}
          disabled={mutation.isPending || !isDirty}
          onClick={() => mutation.mutate()}
        >
          Uložit
        </Button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
        <span className={styles.description}>{description}</span>
        {saved && (
          <span className={styles.savedHint}>
            <CheckmarkCircle24Regular style={{ fontSize: 14 }} /> Uloženo
          </span>
        )}
        {mutation.isError && (
          <span style={{ fontSize: '12px', color: tokens.colorPaletteRedForeground1 }}>
            Chyba při ukládání
          </span>
        )}
      </div>
    </div>
  )
}

interface HrEmailItemProps {
  configKey: string
  currentValue: string
}

function HrEmailItem({ configKey, currentValue }: HrEmailItemProps) {
  const styles = useStyles()
  const qc = useQueryClient()
  const [value, setValue] = useState(currentValue)
  const [saved, setSaved] = useState(false)

  const { data: empData } = useQuery({
    queryKey: ['employees-all-for-hr'],
    queryFn: () => employeesService.getAll({ pageSize: 500 }),
    staleTime: 5 * 60_000,
  })

  const { data: contractorData } = useQuery({
    queryKey: ['contractors-all-for-hr'],
    queryFn: () => contractorsService.getAll({ pageSize: 500 }),
    staleTime: 5 * 60_000,
  })

  const mutation = useMutation({
    mutationFn: () => configService.upsert(configKey, value),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['config'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })

  const isDirty = value !== currentValue

  const employees = empData?.items ?? []
  const contractors = contractorData?.items ?? []

  return (
    <div>
      <div className={styles.row}>
        <Combobox
          freeform
          placeholder="— vyberte nebo zadejte e-mail —"
          value={value}
          onInput={e => { setValue((e.target as HTMLInputElement).value); setSaved(false) }}
          onOptionSelect={(_, data) => {
            if (data.optionValue) { setValue(data.optionValue); setSaved(false) }
          }}
          style={{ flex: 1 }}
        >
          {employees.map(emp => (
            <Option key={`emp-${emp.id}`} value={emp.email} text={emp.email}>
              {emp.fullName} ({emp.email})
            </Option>
          ))}
          {contractors
            .filter(c => c.orbitEmail)
            .map(c => (
              <Option key={`con-${c.id}`} value={c.orbitEmail!} text={c.orbitEmail!}>
                {c.contactFirstName} {c.contactLastName} (externista)
              </Option>
            ))}
        </Combobox>
        <Button
          appearance="primary"
          icon={mutation.isPending ? <Spinner size="tiny" /> : <Save24Regular />}
          disabled={mutation.isPending || !isDirty}
          onClick={() => mutation.mutate()}
        >
          Uložit
        </Button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
        {saved && (
          <span className={styles.savedHint}>
            <CheckmarkCircle24Regular style={{ fontSize: 14 }} /> Uloženo
          </span>
        )}
        {mutation.isError && (
          <span style={{ fontSize: '12px', color: tokens.colorPaletteRedForeground1 }}>
            Chyba při ukládání
          </span>
        )}
      </div>
    </div>
  )
}

const DEFAULT_HEALTH_INSURANCES: Record<string, string> = {
  '111': 'VZP ČR',
  '201': 'Vojenská zdravotní pojišťovna',
  '205': 'ČPZP',
  '207': 'Oborová zdravotní pojišťovna',
  '209': 'Zaměstnanecká pojišťovna Škoda',
  '211': 'Zdravotní pojišťovna MV ČR',
  '213': 'RBP, zdravotní pojišťovna',
}

function HealthInsurancesItem({ currentValue }: { currentValue: string }) {
  const styles = useStyles()
  const qc = useQueryClient()
  const [saved, setSaved] = useState(false)

  const parseRows = (v: string): { code: string; name: string }[] => {
    try {
      const obj = JSON.parse(v || '{}') as Record<string, string>
      return Object.entries(obj).map(([code, name]) => ({ code, name }))
    } catch {
      return Object.entries(DEFAULT_HEALTH_INSURANCES).map(([code, name]) => ({ code, name }))
    }
  }

  const [rows, setRows] = useState(() => parseRows(currentValue))

  useEffect(() => { setRows(parseRows(currentValue)) }, [currentValue])

  const toJson = (r: { code: string; name: string }[]) =>
    JSON.stringify(Object.fromEntries(r.filter(x => x.code.trim()).map(x => [x.code.trim(), x.name.trim()])))

  const currentJson = toJson(parseRows(currentValue))
  const isDirty = toJson(rows) !== currentJson

  const mutation = useMutation({
    mutationFn: () => configService.upsert(CONFIG_KEYS.HealthInsurances, toJson(rows)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['config'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })

  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: tokens.colorNeutralForeground3, paddingBottom: 4, width: 90 }}>Kód</th>
            <th style={{ textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: tokens.colorNeutralForeground3, paddingBottom: 4 }}>Název</th>
            <th style={{ width: 32 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td style={{ paddingRight: 8, paddingBottom: 6 }}>
                <input
                  className={styles.input}
                  value={row.code}
                  maxLength={3}
                  onChange={e => setRows(r => r.map((x, j) => j === i ? { ...x, code: e.target.value } : x))}
                  style={{ width: 70 }}
                />
              </td>
              <td style={{ paddingBottom: 6 }}>
                <input
                  className={styles.input}
                  value={row.name}
                  onChange={e => setRows(r => r.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                />
              </td>
              <td style={{ paddingBottom: 6, paddingLeft: 8 }}>
                <Button
                  appearance="subtle"
                  icon={<Delete24Regular />}
                  size="small"
                  onClick={() => setRows(r => r.filter((_, j) => j !== i))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={styles.row}>
        <Button
          appearance="subtle"
          icon={<Add24Regular />}
          size="small"
          onClick={() => setRows(r => [...r, { code: '', name: '' }])}
        >
          Přidat řádek
        </Button>
        <div style={{ flex: 1 }} />
        <Button
          appearance="primary"
          icon={mutation.isPending ? <Spinner size="tiny" /> : <Save24Regular />}
          disabled={mutation.isPending || !isDirty}
          onClick={() => mutation.mutate()}
        >
          Uložit
        </Button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
        {saved && (
          <span className={styles.savedHint}>
            <CheckmarkCircle24Regular style={{ fontSize: 14 }} /> Uloženo
          </span>
        )}
        {mutation.isError && (
          <span style={{ fontSize: '12px', color: tokens.colorPaletteRedForeground1 }}>
            Chyba při ukládání
          </span>
        )}
      </div>
    </div>
  )
}

const DEFAULT_SUBJECTS: Record<string, string> = {
  EmailTemplate_SignatureReminder_Subject:       'Připomínka: Podpis dokumentu – {{nazevDokumentu}}',
  EmailTemplate_AcquaintanceReminder_Subject:    'Připomínka: Seznámení s dokumentem – {{nazevDokumentu}}',
  EmailTemplate_LmsReminder_Subject:             'Připomínka: Školení LMS – {{nazevDokumentu}}',
  EmailTemplate_SignatureNotification_Subject:   'Podpis dokumentu – {{nazevDokumentu}}',
  EmailTemplate_AcquaintanceNotification_Subject:'Seznámení s dokumentem – {{nazevDokumentu}}',
  EmailTemplate_LmsNotification_Subject:         'Školení LMS – {{nazevDokumentu}}',
}

const DEFAULT_BODIES: Record<string, string> = {
  EmailTemplate_SignatureReminder_Body:
    '<p>Dobrý den,</p><p>dovolujeme si Vás připomenout, že dosud nebyl dokument <strong>„{{nazevDokumentu}}"</strong> podepsán.</p><p>Termín: {{termin}}</p><p>{{odkazNaDokument}}</p><p>Prosím potvrďte splnění prostřednictvím systému Personalistika nebo kontaktujte HR.</p><p>S pozdravem<br>HR oddělení</p>',
  EmailTemplate_AcquaintanceReminder_Body:
    '<p>Dobrý den,</p><p>dovolujeme si Vás připomenout, že dosud nebyl dokument <strong>„{{nazevDokumentu}}"</strong> potvrzen (seznámení).</p><p>Termín: {{termin}}</p><p>{{odkazNaDokument}}</p><p>Prosím potvrďte splnění prostřednictvím systému Personalistika nebo kontaktujte HR.</p><p>S pozdravem<br>HR oddělení</p>',
  EmailTemplate_SignatureNotification_Body:
    '<p>Dobrý den,</p><p>žádáme Vás o podpis dokumentu <strong>„{{nazevDokumentu}}"</strong>.</p><p>Termín: {{termin}}</p><p>{{odkazNaDokument}}</p><p>Přijďte dokument podepsat nebo kontaktujte HR.</p><p>S pozdravem<br>HR oddělení</p>',
  EmailTemplate_AcquaintanceNotification_Body:
    '<p>Dobrý den,</p><p>žádáme Vás, abyste si pročetli a potvrdili seznámení s dokumentem <strong>„{{nazevDokumentu}}"</strong>.</p><p>Termín: {{termin}}</p><p>{{odkazNaDokument}}</p><p>Prosím dokument pročtěte a potvrďte splnění prostřednictvím systému Personalistika nebo kontaktujte HR.</p><p>S pozdravem<br>HR oddělení</p>',
  EmailTemplate_LmsReminder_Body:
    '<p>Dobrý den,</p><p>dovolujeme si Vás připomenout, že dosud nebylo školení <strong>„{{nazevDokumentu}}"</strong> absolvováno v systému LMS.</p><p>Termín: {{termin}}</p><p>{{odkazNaDokument}}</p><p>Po absolvování bude Váš výsledek automaticky importován.</p><p>S pozdravem<br>HR oddělení</p>',
  EmailTemplate_LmsNotification_Body:
    '<p>Dobrý den,</p><p>byl Vám přiřazen povinný e-learningový kurz <strong>„{{nazevDokumentu}}"</strong> v systému LMS skupiny SUDOP CIT.</p><p>Termín: {{termin}}</p><p>{{odkazNaDokument}}</p><p>Po absolvování kurzu bude Váš výsledek automaticky importován do systému People.</p><p>S pozdravem<br>HR oddělení</p>',
}

interface EmailTemplateItemProps {
  label: string
  subjectKey: string
  bodyKey: string
  currentSubject: string
  currentBody: string
}

function EmailTemplateItem({ label, subjectKey, bodyKey, currentSubject, currentBody }: EmailTemplateItemProps) {
  const styles = useStyles()
  const qc = useQueryClient()

  const defaultSubject = currentSubject || DEFAULT_SUBJECTS[subjectKey] || ''
  const defaultBody = currentBody || DEFAULT_BODIES[bodyKey] || ''

  const [subject, setSubject] = useState(defaultSubject)
  const [body, setBody] = useState(defaultBody)
  const [saved, setSaved] = useState(false)

  // Synchronizace pokud se změní props (po načtení configu z API)
  useEffect(() => { setSubject(currentSubject || DEFAULT_SUBJECTS[subjectKey] || '') }, [currentSubject, subjectKey])
  useEffect(() => { setBody(currentBody || DEFAULT_BODIES[bodyKey] || '') }, [currentBody, bodyKey])

  const isDirty = subject !== (currentSubject || DEFAULT_SUBJECTS[subjectKey] || '')
    || body !== (currentBody || DEFAULT_BODIES[bodyKey] || '')

  const mutation = useMutation({
    mutationFn: async () => {
      await configService.upsert(subjectKey, subject)
      await configService.upsert(bodyKey, body)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['config'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })

  return (
    <div className={styles.templateSection}>
      <div className={styles.templateLabel}>{label}</div>
      <div className={styles.varHint}>
        Proměnné: {'{{nazevDokumentu}}'} &nbsp;{'{{termin}}'} &nbsp;{'{{odkazNaDokument}}'}
      </div>

      <div className={styles.label}>Předmět</div>
      <div className={styles.row} style={{ marginBottom: 16 }}>
        <input
          className={styles.input}
          value={subject}
          onChange={e => { setSubject(e.target.value); setSaved(false) }}
          style={{ flex: 1 }}
        />
      </div>

      <div className={styles.label}>Tělo e-mailu</div>
      <div style={{ marginBottom: 10 }}>
        <EmailHtmlEditor value={body} onChange={v => { setBody(v); setSaved(false) }} />
      </div>

      <div className={styles.row}>
        <Button
          appearance="primary"
          icon={mutation.isPending ? <Spinner size="tiny" /> : <Save24Regular />}
          disabled={mutation.isPending || !isDirty}
          onClick={() => mutation.mutate()}
        >
          Uložit šablonu
        </Button>
        {saved && (
          <span className={styles.savedHint}>
            <CheckmarkCircle24Regular style={{ fontSize: 14 }} /> Uloženo
          </span>
        )}
        {mutation.isError && (
          <span style={{ fontSize: '12px', color: tokens.colorPaletteRedForeground1 }}>
            Chyba při ukládání
          </span>
        )}
      </div>
    </div>
  )
}

function formatCzDate(iso: string): string {
  const [, m, d] = iso.split('-')
  return `${parseInt(d)}. ${parseInt(m)}.`
}

const CZ_MONTHS = ['ledna','února','března','dubna','května','června','července','srpna','září','října','listopadu','prosince']

function formatCzDateFull(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${parseInt(d)}. ${CZ_MONTHS[parseInt(m) - 1]} ${y}`
}

function PublicHolidaysSection() {
  const styles = useStyles()
  const qc = useQueryClient()
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)
  const [addDate, setAddDate] = useState('')
  const [addName, setAddName] = useState('')
  const [addError, setAddError] = useState('')
  const [editId, setEditId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [generateOpen, setGenerateOpen] = useState(false)

  const { data: holidays, isLoading } = useQuery({
    queryKey: ['public-holidays', year],
    queryFn: () => publicHolidaysService.getByYear(year),
  })

  const createMutation = useMutation({
    mutationFn: () => publicHolidaysService.create(addDate, addName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['public-holidays', year] })
      setAddDate(''); setAddName(''); setAddError('')
    },
    onError: () => setAddError('Svátek pro toto datum již existuje nebo nastala chyba.'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => publicHolidaysService.update(id, name),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['public-holidays', year] }); setEditId(null) },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => publicHolidaysService.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['public-holidays', year] }); setDeleteId(null) },
  })

  const generateMutation = useMutation({
    mutationFn: () => publicHolidaysService.generate(year),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['public-holidays', year] }); setGenerateOpen(false) },
  })

  const yearOptions = Array.from({ length: 7 }, (_, i) => currentYear - 1 + i)
  const sorted = [...(holidays ?? [])].sort((a, b) => a.date.localeCompare(b.date))

  const deleteTarget = deleteId != null ? sorted.find(h => h.id === deleteId) : null

  return (
    <div>
      {/* Rok picker + tlačítko generovat */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div className={styles.label} style={{ marginBottom: 0 }}>Rok:</div>
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          className={styles.input}
          style={{ width: 90 }}
        >
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <Button
          appearance="subtle"
          icon={generateMutation.isPending ? <Spinner size="tiny" /> : <CalendarLtr24Regular />}
          onClick={() => setGenerateOpen(true)}
          disabled={generateMutation.isPending}
        >
          Načíst CZ svátky pro {year}
        </Button>
      </div>

      {/* Tabulka svátků */}
      {isLoading ? <Spinner size="tiny" label="Načítám..." /> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: tokens.colorNeutralForeground3, paddingBottom: 4, width: 90 }}>Datum</th>
              <th style={{ textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: tokens.colorNeutralForeground3, paddingBottom: 4 }}>Název</th>
              <th style={{ width: 32 }} />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={3} style={{ fontSize: 13, color: tokens.colorNeutralForeground3, padding: '8px 0' }}>
                Žádné svátky pro rok {year}. Klikněte na „Načíst CZ svátky".
              </td></tr>
            )}
            {sorted.map(h => (
              <tr key={h.id}>
                <td style={{ paddingRight: 12, paddingBottom: 6, fontSize: 13, whiteSpace: 'nowrap', color: tokens.colorNeutralForeground2 }}>
                  {formatCzDate(h.date)}
                </td>
                <td style={{ paddingBottom: 6 }}>
                  {editId === h.id ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        className={styles.input}
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        autoFocus
                        style={{ flex: 1 }}
                      />
                      <Button size="small" appearance="primary"
                        disabled={updateMutation.isPending}
                        icon={updateMutation.isPending ? <Spinner size="tiny" /> : <Save24Regular />}
                        onClick={() => updateMutation.mutate({ id: h.id, name: editName })}
                      >Uložit</Button>
                      <Button size="small" appearance="subtle" onClick={() => setEditId(null)}>Zrušit</Button>
                    </div>
                  ) : (
                    <span
                      style={{ fontSize: 13, cursor: 'pointer' }}
                      title="Klikněte pro úpravu názvu"
                      onClick={() => { setEditId(h.id); setEditName(h.name) }}
                    >{h.name}</span>
                  )}
                </td>
                <td style={{ paddingBottom: 6, paddingLeft: 8 }}>
                  <Button
                    appearance="subtle"
                    icon={<Delete24Regular />}
                    size="small"
                    onClick={() => setDeleteId(h.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Přidat svátek */}
      <div style={{ borderTop: `1px solid ${tokens.colorNeutralStroke2}`, paddingTop: 12, marginTop: 4 }}>
        <div className={styles.label}>Přidat svátek</div>
        <div className={styles.row}>
          <input
            type="date"
            className={styles.input}
            value={addDate}
            min={`${year}-01-01`}
            max={`${year}-12-31`}
            onChange={e => { setAddDate(e.target.value); setAddError('') }}
            style={{ width: 150 }}
          />
          <input
            className={styles.input}
            placeholder="Název svátku"
            value={addName}
            onChange={e => { setAddName(e.target.value); setAddError('') }}
            style={{ flex: 1 }}
          />
          <Button
            appearance="primary"
            icon={createMutation.isPending ? <Spinner size="tiny" /> : <Add24Regular />}
            disabled={createMutation.isPending || !addDate || !addName.trim()}
            onClick={() => createMutation.mutate()}
          >Přidat</Button>
        </div>
        {addError && <div style={{ fontSize: 12, color: tokens.colorPaletteRedForeground1, marginTop: 4 }}>{addError}</div>}
      </div>

      {/* Dialog: potvrdit smazání */}
      <Dialog open={deleteId !== null} onOpenChange={(_, d) => { if (!d.open) setDeleteId(null) }}>
        <DialogSurface style={{ maxWidth: 400 }}>
          <DialogTitle>Smazat svátek</DialogTitle>
          <DialogBody>
            <Text>Opravdu smazat <strong>{deleteTarget ? formatCzDateFull(deleteTarget.date) : ''}</strong> ({deleteTarget?.name})?</Text>
          </DialogBody>
          <DialogActions>
            <Button appearance="subtle" onClick={() => setDeleteId(null)}>Zrušit</Button>
            <Button
              appearance="primary"
              style={{ background: tokens.colorPaletteRedBackground3 }}
              icon={deleteMutation.isPending ? <Spinner size="tiny" /> : undefined}
              disabled={deleteMutation.isPending}
              onClick={() => deleteId != null && deleteMutation.mutate(deleteId)}
            >Smazat</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      {/* Dialog: generovat svátky */}
      <Dialog open={generateOpen} onOpenChange={(_, d) => { if (!d.open) setGenerateOpen(false) }}>
        <DialogSurface style={{ maxWidth: 420 }}>
          <DialogTitle>Načíst CZ svátky pro rok {year}</DialogTitle>
          <DialogBody>
            <Text>
              Přidá standardní státní svátky ČR pro rok <strong>{year}</strong> (celkem 13 svátků včetně Velikonočního pondělí).
              Svátky, které již existují, budou přeskočeny.
            </Text>
            {generateMutation.isSuccess && (
              <div style={{ marginTop: 12, fontSize: 13, color: tokens.colorPaletteGreenForeground1 }}>
                ✓ Přidáno: {generateMutation.data?.created}, přeskočeno: {generateMutation.data?.skipped}
              </div>
            )}
          </DialogBody>
          <DialogActions>
            <Button appearance="subtle" onClick={() => setGenerateOpen(false)}>Zrušit</Button>
            <Button
              appearance="primary"
              icon={generateMutation.isPending ? <Spinner size="tiny" /> : undefined}
              disabled={generateMutation.isPending}
              onClick={() => generateMutation.mutate()}
            >Načíst svátky</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  )
}

function VocativeExceptionsSection() {
  const styles = useStyles()
  const qc = useQueryClient()
  const [addFirstName, setAddFirstName] = useState('')
  const [addVocative, setAddVocative] = useState('')
  const [addError, setAddError] = useState('')
  const [editId, setEditId] = useState<number | null>(null)
  const [editFirstName, setEditFirstName] = useState('')
  const [editVocative, setEditVocative] = useState('')
  const [deleteId, setDeleteId] = useState<number | null>(null)

  const { data: exceptions, isLoading } = useQuery({
    queryKey: ['vocative-exceptions'],
    queryFn: vocativeExceptionsService.getAll,
  })

  const createMutation = useMutation({
    mutationFn: () => vocativeExceptionsService.create(addFirstName.trim(), addVocative.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vocative-exceptions'] })
      setAddFirstName(''); setAddVocative(''); setAddError('')
    },
    onError: () => setAddError('Výjimka pro toto jméno již existuje nebo nastala chyba.'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, firstName, vocative }: { id: number; firstName: string; vocative: string }) =>
      vocativeExceptionsService.update(id, firstName, vocative),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vocative-exceptions'] })
      setEditId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => vocativeExceptionsService.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vocative-exceptions'] })
      setDeleteId(null)
    },
  })

  const deleteTarget = deleteId != null ? (exceptions ?? []).find((e: VocativeException) => e.id === deleteId) : null

  return (
    <div>
      {isLoading ? <Spinner size="tiny" label="Načítám..." /> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: tokens.colorNeutralForeground3, paddingBottom: 4, width: '45%' }}>Křestní jméno</th>
              <th style={{ textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: tokens.colorNeutralForeground3, paddingBottom: 4 }}>Oslovení (vokativ)</th>
              <th style={{ width: 32 }} />
            </tr>
          </thead>
          <tbody>
            {(exceptions ?? []).length === 0 && (
              <tr><td colSpan={3} style={{ fontSize: 13, color: tokens.colorNeutralForeground3, padding: '8px 0' }}>
                Žádné výjimky. Přidejte první výjimku níže.
              </td></tr>
            )}
            {(exceptions ?? []).map((exc: VocativeException) => (
              <tr key={exc.id}>
                <td style={{ paddingRight: 12, paddingBottom: 6 }}>
                  {editId === exc.id ? (
                    <input
                      className={styles.input}
                      value={editFirstName}
                      onChange={e => setEditFirstName(e.target.value)}
                      autoFocus
                    />
                  ) : (
                    <span
                      style={{ fontSize: 13, cursor: 'pointer' }}
                      title="Klikněte pro úpravu"
                      onClick={() => { setEditId(exc.id); setEditFirstName(exc.firstName); setEditVocative(exc.vocative) }}
                    >{exc.firstName}</span>
                  )}
                </td>
                <td style={{ paddingBottom: 6 }}>
                  {editId === exc.id ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        className={styles.input}
                        value={editVocative}
                        onChange={e => setEditVocative(e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <Button size="small" appearance="primary"
                        disabled={updateMutation.isPending}
                        icon={updateMutation.isPending ? <Spinner size="tiny" /> : <Save24Regular />}
                        onClick={() => updateMutation.mutate({ id: exc.id, firstName: editFirstName, vocative: editVocative })}
                      >Uložit</Button>
                      <Button size="small" appearance="subtle" onClick={() => setEditId(null)}>Zrušit</Button>
                    </div>
                  ) : (
                    <span
                      style={{ fontSize: 13, cursor: 'pointer' }}
                      title="Klikněte pro úpravu"
                      onClick={() => { setEditId(exc.id); setEditFirstName(exc.firstName); setEditVocative(exc.vocative) }}
                    >{exc.vocative}</span>
                  )}
                </td>
                <td style={{ paddingBottom: 6, paddingLeft: 8 }}>
                  <Button
                    appearance="subtle"
                    icon={<Delete24Regular />}
                    size="small"
                    onClick={() => setDeleteId(exc.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Přidat výjimku */}
      <div style={{ borderTop: `1px solid ${tokens.colorNeutralStroke2}`, paddingTop: 12, marginTop: 4 }}>
        <div className={styles.label}>Přidat výjimku</div>
        <div className={styles.row}>
          <input
            className={styles.input}
            placeholder="Křestní jméno"
            value={addFirstName}
            onChange={e => { setAddFirstName(e.target.value); setAddError('') }}
            style={{ flex: 1 }}
          />
          <input
            className={styles.input}
            placeholder="Oslovení (vokativ)"
            value={addVocative}
            onChange={e => { setAddVocative(e.target.value); setAddError('') }}
            style={{ flex: 1 }}
          />
          <Button
            appearance="primary"
            icon={createMutation.isPending ? <Spinner size="tiny" /> : <Add24Regular />}
            disabled={createMutation.isPending || !addFirstName.trim() || !addVocative.trim()}
            onClick={() => createMutation.mutate()}
          >Přidat</Button>
        </div>
        {addError && <div style={{ fontSize: 12, color: tokens.colorPaletteRedForeground1, marginTop: 4 }}>{addError}</div>}
      </div>

      {/* Dialog: potvrdit smazání */}
      <Dialog open={deleteId !== null} onOpenChange={(_, d) => { if (!d.open) setDeleteId(null) }}>
        <DialogSurface style={{ maxWidth: 400 }}>
          <DialogTitle>Smazat výjimku</DialogTitle>
          <DialogBody>
            <Text>Opravdu smazat výjimku pro jméno <strong>{deleteTarget?.firstName}</strong> (oslovení: {deleteTarget?.vocative})?</Text>
          </DialogBody>
          <DialogActions>
            <Button appearance="subtle" onClick={() => setDeleteId(null)}>Zrušit</Button>
            <Button
              appearance="primary"
              style={{ background: tokens.colorPaletteRedBackground3 }}
              icon={deleteMutation.isPending ? <Spinner size="tiny" /> : undefined}
              disabled={deleteMutation.isPending}
              onClick={() => deleteId != null && deleteMutation.mutate(deleteId)}
            >Smazat</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  )
}

export default function AdminPage() {
  const styles = useStyles()
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const toggleSection = (key: string) => setOpenSections(s => ({ ...s, [key]: !s[key] }))

  const { data: configs, isLoading, isError } = useQuery({
    queryKey: ['config'],
    queryFn: configService.getAll,
  })

  const getValue = (key: string) =>
    configs?.find((c: { key: string }) => c.key === key)?.value ?? ''

  if (isLoading) return <Spinner label="Načítám nastavení..." />

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Settings24Regular style={{ fontSize: 28, color: tokens.colorBrandForeground1 }} />
        <Text size={700} weight="semibold">Administrace</Text>
      </div>

      {isError && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>Nepodařilo se načíst konfiguraci.</MessageBarBody>
        </MessageBar>
      )}

      {/* Sekce 1 — SharePoint */}
      <div className={styles.card}>
        <div
          className={styles.cardHeader}
          onClick={() => toggleSection('sharepoint')}
          style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Text className={styles.cardTitle}>SharePoint — výchozí složky</Text>
          {openSections.sharepoint ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
        </div>
        {openSections.sharepoint && (
          <div className={styles.cardBody}>
            <ConfigItem
              configKey={CONFIG_KEYS.SharePointUrl_EmployeeActive}
              label="Zaměstnanec aktivní / nenastoupil / rodičovská"
              description="Fallback odkaz pro aktivní zaměstnance bez vlastní SharePoint složky."
              currentValue={getValue(CONFIG_KEYS.SharePointUrl_EmployeeActive)}
            />
            <ConfigItem
              configKey={CONFIG_KEYS.SharePointUrl_EmployeeTerminated}
              label="Zaměstnanec ukončen"
              description="Fallback odkaz pro ukončené zaměstnance."
              currentValue={getValue(CONFIG_KEYS.SharePointUrl_EmployeeTerminated)}
            />
            <ConfigItem
              configKey={CONFIG_KEYS.SharePointUrl_ContractorActive}
              label="Externista aktivní"
              description="Fallback odkaz pro aktivní externisty."
              currentValue={getValue(CONFIG_KEYS.SharePointUrl_ContractorActive)}
            />
            <ConfigItem
              configKey={CONFIG_KEYS.SharePointUrl_ContractorInactive}
              label="Externista neaktivní"
              description="Fallback odkaz pro neaktivní externisty."
              currentValue={getValue(CONFIG_KEYS.SharePointUrl_ContractorInactive)}
            />
          </div>
        )}
      </div>

      {/* Sekce 2 — Dovolená */}
      <div className={styles.card}>
        <div
          className={styles.cardHeader}
          onClick={() => toggleSection('vacation')}
          style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Text className={styles.cardTitle}>Dovolená</Text>
          {openSections.vacation ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
        </div>
        {openSections.vacation && (
          <div className={styles.cardBody}>
            <ConfigItem
              configKey={CONFIG_KEYS.VacationDaysDefault}
              label="Výchozí počet dní dovolené (plný úvazek)"
              description="Výchozí roční nárok v dnech pro HPP 100 %. Změna se projeví pro nový kalendářní rok. Pro aktuální rok spusťte Přepočítat v sekci Nepřítomnosti."
              currentValue={getValue(CONFIG_KEYS.VacationDaysDefault)}
            />
            <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
              Pro ostatní zaměstnance (částečný úvazek, DPP, DPČ) je nárok poměrný. Ruční úpravy dovolené v průběhu roku musí HR provést ručně v detailu zaměstnance.
            </Text>
          </div>
        )}
      </div>

      {/* Sekce 3 — Zdravotní pojišťovny */}
      <div className={styles.card}>
        <div
          className={styles.cardHeader}
          onClick={() => toggleSection('healthInsurances')}
          style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Text className={styles.cardTitle}>Zdravotní pojišťovny</Text>
          {openSections.healthInsurances ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
        </div>
        {openSections.healthInsurances && (
          <div className={styles.cardBody}>
            <HealthInsurancesItem currentValue={getValue(CONFIG_KEYS.HealthInsurances)} />
          </div>
        )}
      </div>

      {/* Sekce 5 — HR manažeři */}
      <div className={styles.card}>
        <div
          className={styles.cardHeader}
          onClick={() => toggleSection('hrManagers')}
          style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Text className={styles.cardTitle}>HR manažeři</Text>
          {openSections.hrManagers ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
        </div>
        {openSections.hrManagers && (
          <div className={styles.cardBody}>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              E-maily HR manažerů, kteří dostávají upozornění (dokumenty, expirace smluv apod.). Lze zadat až 3.
            </Text>
            <HrEmailItem
              configKey={CONFIG_KEYS.HrManagerEmail1}
              currentValue={getValue(CONFIG_KEYS.HrManagerEmail1)}
            />
            <HrEmailItem
              configKey={CONFIG_KEYS.HrManagerEmail2}
              currentValue={getValue(CONFIG_KEYS.HrManagerEmail2)}
            />
            <HrEmailItem
              configKey={CONFIG_KEYS.HrManagerEmail3}
              currentValue={getValue(CONFIG_KEYS.HrManagerEmail3)}
            />
          </div>
        )}
      </div>

      {/* Sekce — Výjimky v oslovení jménem */}
      <div className={styles.card}>
        <div
          className={styles.cardHeader}
          onClick={() => toggleSection('vocativeExceptions')}
          style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Text className={styles.cardTitle}>Výjimky v oslovení jménem</Text>
          {openSections.vocativeExceptions ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
        </div>
        {openSections.vocativeExceptions && (
          <div className={styles.cardBody}>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              Výjimky pro skloňování křestního jména do 5. pádu (vokativu) při oslovení v e-mailech.
              Pokud jméno není v seznamu, použijí se automatická pravidla.
            </Text>
            <VocativeExceptionsSection />
          </div>
        )}
      </div>

      {/* Sekce 4 — Státní svátky */}
      <div className={styles.card}>
        <div
          className={styles.cardHeader}
          onClick={() => toggleSection('publicHolidays')}
          style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Text className={styles.cardTitle} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarLtr24Regular style={{ fontSize: 18 }} />
            Státní svátky
          </Text>
          {openSections.publicHolidays ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
        </div>
        {openSections.publicHolidays && (
          <div className={styles.cardBody}>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              Správa státních svátků ČR. Svátky ovlivňují výpočet pracovních dnů v žádostech o nepřítomnost a export docházky.
              Kliknutím na název svátku jej upravíte.
            </Text>
            <PublicHolidaysSection />
          </div>
        )}
      </div>

      {/* Sekce 6 — E-mailové šablony dokumentů */}
      <div className={styles.card}>
        <div
          className={styles.cardHeader}
          onClick={() => toggleSection('emailTemplates')}
          style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Text className={styles.cardTitle} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Mail24Regular style={{ fontSize: 18 }} />
            E-mailové šablony dokumentů
          </Text>
          {openSections.emailTemplates ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
        </div>
        {openSections.emailTemplates && (
          <div className={styles.cardBody}>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              Šablony pro e-maily k dokumentům. Pokud nevyplníte, použijí se výchozí texty.
              V šabloně lze použít proměnné <code>{'{{nazevDokumentu}}'}</code>, <code>{'{{termin}}'}</code> a <code>{'{{odkazNaDokument}}'}</code>.
              Tělo e-mailu může obsahovat HTML (tučný text, odstavce, odkazy apod.).
            </Text>

            <EmailTemplateItem
              label="Připomínka – Podpis dokumentu"
              subjectKey={CONFIG_KEYS.EmailTemplate_SignatureReminder_Subject}
              bodyKey={CONFIG_KEYS.EmailTemplate_SignatureReminder_Body}
              currentSubject={getValue(CONFIG_KEYS.EmailTemplate_SignatureReminder_Subject)}
              currentBody={getValue(CONFIG_KEYS.EmailTemplate_SignatureReminder_Body)}
            />
            <EmailTemplateItem
              label="Připomínka – Seznámení s dokumentem"
              subjectKey={CONFIG_KEYS.EmailTemplate_AcquaintanceReminder_Subject}
              bodyKey={CONFIG_KEYS.EmailTemplate_AcquaintanceReminder_Body}
              currentSubject={getValue(CONFIG_KEYS.EmailTemplate_AcquaintanceReminder_Subject)}
              currentBody={getValue(CONFIG_KEYS.EmailTemplate_AcquaintanceReminder_Body)}
            />
            <EmailTemplateItem
              label="Oznámení – Podpis dokumentu"
              subjectKey={CONFIG_KEYS.EmailTemplate_SignatureNotification_Subject}
              bodyKey={CONFIG_KEYS.EmailTemplate_SignatureNotification_Body}
              currentSubject={getValue(CONFIG_KEYS.EmailTemplate_SignatureNotification_Subject)}
              currentBody={getValue(CONFIG_KEYS.EmailTemplate_SignatureNotification_Body)}
            />
            <EmailTemplateItem
              label="Oznámení – Seznámení s dokumentem"
              subjectKey={CONFIG_KEYS.EmailTemplate_AcquaintanceNotification_Subject}
              bodyKey={CONFIG_KEYS.EmailTemplate_AcquaintanceNotification_Body}
              currentSubject={getValue(CONFIG_KEYS.EmailTemplate_AcquaintanceNotification_Subject)}
              currentBody={getValue(CONFIG_KEYS.EmailTemplate_AcquaintanceNotification_Body)}
            />
            <EmailTemplateItem
              label="Připomínka – Školení LMS"
              subjectKey={CONFIG_KEYS.EmailTemplate_LmsReminder_Subject}
              bodyKey={CONFIG_KEYS.EmailTemplate_LmsReminder_Body}
              currentSubject={getValue(CONFIG_KEYS.EmailTemplate_LmsReminder_Subject)}
              currentBody={getValue(CONFIG_KEYS.EmailTemplate_LmsReminder_Body)}
            />
            <EmailTemplateItem
              label="Oznámení – Školení LMS"
              subjectKey={CONFIG_KEYS.EmailTemplate_LmsNotification_Subject}
              bodyKey={CONFIG_KEYS.EmailTemplate_LmsNotification_Body}
              currentSubject={getValue(CONFIG_KEYS.EmailTemplate_LmsNotification_Subject)}
              currentBody={getValue(CONFIG_KEYS.EmailTemplate_LmsNotification_Body)}
            />
          </div>
        )}
      </div>
    </div>
  )
}
